export type TextParserTagDef<T> = {
  pattern: RegExp
  callback: (match: RegExpMatchArray) => string | T
}
/**
 * Parse text into an array of strings and/or values.
 *
 * Regular expressions are here to match for special types of text that should be
 * found and passed to callback to convert into special value that user wants.
 *
 * Example:
 *
 *     parseText(
 *      'Hello, https://www.example.com my name is John Doe and my email is john.doe@example.com',
 *      [
 *        {
 *          regexp: /(\w+)\.(\w+)@(\w+)\.(\w+)/, // email regexp
 *          callback: (match, regexp) => ({ type: 'email', value: match[0] })
 *         },
 *        {
 *          regexp: /https?:\/\/\w+\.\w+\.\w+/, // url regexp
 *          callback: (match, regexp) => ({ type: 'url', value: match[0] })
 *        }
 *      ]
 *     )
 *
 */
export function parseText<T = string>(text: string, tagsParsers: TextParserTagDef<T>[]): Array<string | T> {
  let remainingText = text
  const result: Array<string | T> = []
  while (remainingText != '') {
    const matches = tagsParsers.map(({ pattern, callback }) => ({ match: remainingText.match(pattern), callback }))

    const { match, callback } = matches.reduce((acc, { match, callback }) => {
      if (match && match.index !== undefined && (!acc.match || match.index < (acc.match.index as number))) {
        return { match, callback }
      }
      return acc
    }, matches[0])
    if (!match || match.index === undefined) {
      result.push(remainingText)
      break
    }

    if (match.index > 0) {
      const middle = remainingText.slice(0, match.index)
      result.push(middle)
    }
    result.push(callback(match))
    remainingText = remainingText.slice(match.index + match[0].length)
  }

  return result
}
